import {
  Activity,
  ArrowRight,
  Award,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Coins,
  Copy,
  Download,
  Eye,
  EyeOff,
  Flame,
  Gift,
  Heart,
  History,
  ImagePlus,
  KeyRound,
  LayoutDashboard,
  ListTodo,
  LockKeyhole,
  LogOut,
  Menu,
  MoonStar,
  Moon,
  Package,
  PackageOpen,
  PenLine,
  RotateCw,
  Save,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  TicketCheck,
  TimerReset,
  Trophy,
  Upload,
  X
} from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SUPPORTED_TIMEZONES, TIMEZONE_OPTIONS, streakBonusRate } from "../shared/constants";
import { presentRewardSnapshot } from "../shared/rewards";
import { api, idempotencyKey } from "./api";

type Bootstrap = {
  initialized: boolean;
  setup_protected: boolean;
  timezone: string;
  user_label: string;
  ai_label: string;
};

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

type Overview = {
  statistics: {
    balance: number;
    totalEarned: number;
    totalSpent: number;
    totalPenalties: number;
    currentStreak: number;
    longestStreak: number;
    totalActiveDays: number;
    totalCompletedTasks: number;
    byType: Record<string, number>;
    byDifficulty: Record<string, number>;
  };
  today: { date: string; completed: number; active: boolean };
  calendar: {
    month: string;
    days: Array<{ date: string; completed: number; streak: number }>;
  };
  queues: {
    pending_tasks: number;
    awaiting_review: number;
    pending_redemptions: number;
    awaiting_prizes: number;
  };
  coffret: { open_pools: number; tickets: number; awaiting_fulfilment: number };
  promises: { open: number };
  annotations: { unread: number };
  tonight: { live: number };
  privileges: { active: number };
  recent_achievements: Array<{ achievement: Achievement; unlockedAt: string }>;
  labels: { user: string; ai: string };
  timezone: string;
  boundaries: {
    punishment_intensity: number;
    daily_penalty_limit: number;
    punishments_paused: boolean;
  };
};

type Task = {
  id: string;
  seriesId?: string | null;
  title: string;
  description: string;
  type: "daily" | "sweet" | "challenge" | "surprise";
  difficulty: "easy" | "medium" | "hard";
  basePoints: number;
  status: "pending" | "submitted" | "completed" | "failed" | "expired" | "cancelled";
  verificationMode: "self" | "ai_review";
  proofRequirement: string;
  deadlineAt?: string | null;
  completionDate?: string | null;
  createdAt: string;
  occurrenceDate?: string | null;
  seqDate?: string | null;
  dailySeq?: number | null;
  submissions?: Array<{
    id: string;
    attempt: number;
    proofText: string;
    status: string;
    submittedAt: string;
    reviewedAt?: string | null;
    reviewReason?: string | null;
    assets: Array<{ id: string; width: number; height: number }>;
  }>;
};

type Prize = {
  id: string;
  name: string;
  description: string;
  rarity: "commun" | "rare" | "precieux" | "legendaire";
  points_award: number | null;
  stock: number | null;
  claimed: number;
  remaining: number | null;
  available_until?: string | null;
  available: boolean;
  odds: number | null;
};

type PrizePool = {
  id: string;
  name: string;
  description: string;
  cost_points: number;
  ticket_cost: number;
  show_odds: boolean;
  opens_at?: string | null;
  closes_at?: string | null;
  state: "open" | "upcoming" | "closed";
  prize_count: number;
  tickets_available: number;
  can_draw_with_points: boolean;
  can_draw_with_ticket: boolean;
  items: Prize[];
};

type Draw = {
  id: string;
  poolNameSnapshot: string;
  prizeNameSnapshot: string;
  prizeDescriptionSnapshot: string;
  raritySnapshot: Prize["rarity"];
  oddsSnapshot: number;
  paidWith: "points" | "ticket";
  costPoints: number;
  ticketsUsed: number;
  pointsAwarded: number;
  status: "pending" | "fulfilled" | "cancelled";
  fulfillmentNote?: string | null;
  fulfilledAt?: string | null;
  createdAt: string;
};

type Coffret = {
  balance: number;
  today: string;
  pools: PrizePool[];
  tickets: Array<{ id: string; pool_id: string | null; reason: string; expires_at?: string | null }>;
  draws: Draw[];
};

type Achievement = {
  id: string;
  name: string;
  description: string;
  category: string;
  threshold: number;
  icon: string;
  unlocks?: Array<{ unlockedAt: string }>;
};

/** Les Promesses（spec 第六章）。三个派生标记由服务端算好，前端不重算。 */
type Promise_ = {
  id: string;
  owner: "user" | "AI";
  title: string;
  description: string;
  reward_points: number;
  status: "open" | "kept" | "broken" | "cancelled";
  due_date?: string | null;
  created_at: string;
  settled_at?: string | null;
  settlement_note?: string | null;
  silent_days: number;
  last_progress_on: string | null;
  needs_nudge: boolean;
  report_due: boolean;
  no_progress_yet: boolean;
  overdue: boolean;
};

type Annotation = {
  id: string;
  scope: "daily" | "task" | "promise";
  entryDate?: string | null;
  targetId?: string | null;
  author: "AI" | "user" | "system";
  body: string;
  pinned: boolean;
  readAt?: string | null;
  createdAt: string;
  /** scope='task' 的注记带上它挂在哪一单（后端 join 出来的）。 */
  taskTitle?: string | null;
};

type AnnotationBoard = {
  today: string;
  today_notes: Annotation[];
  pinned: Annotation[];
  recent: Annotation[];
  task_notes?: Annotation[];
  unread: number;
};

type Directive = {
  id: string;
  body: string;
  for_date: string;
  status: "active" | "acknowledged" | "completed" | "released" | "expired";
  acknowledged_at?: string | null;
  released_by_safeword: boolean;
  release_reason?: string | null;
  created_at: string;
};

type PrivilegeUse = {
  id: string;
  name: string;
  cost: number;
  status: "active" | "completed" | "cancelled";
  reason: string;
  detail: string;
  invoked_at: string;
  cancel_reason?: string | null;
};

type PrivilegeBoard = {
  balance: number;
  blocked_by_safeword: boolean;
  items: Array<{ id: string; name: string; description: string; cost: number }>;
  uses: PrivilegeUse[];
  active_count: number;
};

const typeLabel = { daily: "日常", sweet: "甜蜜", challenge: "挑战", surprise: "隐藏" };
/** 任务卡上的法语分类标签（spec 2.2）。 */
const typeLabelFr = {
  daily: "LE QUOTIDIEN",
  sweet: "LA DOUCEUR",
  challenge: "LE DÉFI",
  surprise: "LA SURPRISE"
};
const difficultyLabel = { easy: "轻盈", medium: "认真", hard: "困难" };

/** Le Coffret 的四档稀有度。法语印在角标上，中文给一眼看懂用。 */
const rarityLabel = {
  commun: { fr: "COMMUN", zh: "普通" },
  rare: { fr: "RARE", zh: "稀有" },
  precieux: { fr: "PRÉCIEUX", zh: "珍贵" },
  legendaire: { fr: "LÉGENDAIRE", zh: "传说" }
} as const;

/**
 * 安全词「红灯」（spec 4.4）：无条件、无延迟地终止所有惩罚。
 * 走独立的 /safeword 接口，不经过设置表单，避免被过期表单状态干扰。
 */
function SafeWord({ paused, onChanged }: { paused: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggle(engage: boolean) {
    setBusy(true);
    setError("");
    try {
      await api("/safeword", { method: "POST", body: { engage } });
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "操作失败。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="safeword-block">
      <button
        type="button"
        className={`safeword${paused ? " safeword--engaged" : ""}`}
        disabled={busy}
        onClick={() => toggle(!paused)}
      >
        <ShieldCheck size={15} />
        {paused ? "解除「红灯」，恢复惩罚" : "安全词「红灯」· 立即终止所有惩罚"}
      </button>
      <p className="safeword-note">
        {paused ? (
          <>
            <strong>红灯已亮。</strong>所有惩罚已终止，克部长主动发起的判定和扣分会被服务端拒绝。
            确认自己缓过来了，再按上面的按钮解除。
          </>
        ) : (
          <>
            任何时候都可以按下 <strong>红灯</strong>：无条件、无延迟、不需要理由。
            按下后惩罚立即停止，并记入审计日志。
          </>
        )}
      </p>
      {error && <p className="inline-error">{error}</p>}
    </div>
  );
}

/** 首页 streak 卡的一句话提示：当前档位，以及离下一档还差几天。 */
function streakBonusHint(streak: number): string {
  const rate = streakBonusRate(streak);
  const next = [3, 7, 14, 30].find((day) => day > streak);
  const current = rate > 0 ? `当前 +${Math.round(rate * 100)}%` : "连续 3 天起有加成";
  if (!next) return current;
  return `${current} · 再 ${next - streak} 天到 +${Math.round(streakBonusRate(next) * 100)}%`;
}

const TYPE_INITIAL = { daily: "Q", sweet: "D", challenge: "F", surprise: "S" };

/**
 * 工单编号，形如 Q-0731-03（spec 2.1「任务卡有编号」）。
 * 后两位是服务端按本地日期分区发的真流水号——Q-0731-03 读作
 * 「7 月 31 日第 3 号工单，日常类」，全类型共用一条流水。
 *
 * 兜底那段留给还没回填过流水号的旧任务：拿 id 推一个稳定码，
 * 保证同一条任务永远显示同一个编号，只是它不表示当天第几号。
 */
function taskReference(task: Task): string {
  const date = (task.seqDate ?? task.occurrenceDate ?? task.createdAt).slice(5, 10).replace("-", "");
  if (task.dailySeq) {
    return `${TYPE_INITIAL[task.type]}-${date}-${String(task.dailySeq).padStart(2, "0")}`;
  }
  let hash = 0;
  for (const char of task.id) hash = (hash * 31 + char.charCodeAt(0)) % 1296;
  return `${TYPE_INITIAL[task.type]}-${date}-${hash.toString(36).toUpperCase().padStart(2, "0")}`;
}

function TaskTypeIcon({ type }: { type: Task["type"] }) {
  if (type === "daily") return <CalendarDays />;
  if (type === "sweet") return <Heart />;
  if (type === "challenge") return <Flame />;
  return <Gift />;
}
const statusLabel = {
  pending: "待完成",
  submitted: "待确认",
  completed: "已完成",
  failed: "未通过",
  expired: "已逾期",
  cancelled: "已取消"
};

const supportedTimezoneValues = new Set<string>(SUPPORTED_TIMEZONES);

function defaultTimezone() {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return supportedTimezoneValues.has(detected) ? detected : "Asia/Shanghai";
}

function formatDate(value?: string | null, withTime = false) {
  if (!value) return "没有期限";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {})
  }).format(new Date(value));
}

function pointsFor(task: Task) {
  return task.basePoints * ({ easy: 1, medium: 2, hard: 3 }[task.difficulty] ?? 1);
}

function FullPageLoader() {
  return (
    <div className="splash">
      <div className="phosphene-mark phosphene-mark--large" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <p>正在唤醒你的私人光点…</p>
    </div>
  );
}

function AuthBackdrop({ children, eyebrow, title, copy }: { children: ReactNode; eyebrow: string; title: string; copy: string }) {
  return (
    <main className="auth-page">
      <div className="auth-ambient ambient-one" />
      <div className="auth-ambient ambient-two" />
      <section className="auth-story">
        <div className="brand-lockup brand-lockup--light">
          <div className="phosphene-mark">
            <span />
            <span />
            <span />
          </div>
          <span>L'APPRÉCIATION</span>
        </div>
        <div className="auth-story__copy">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{copy}</p>
        </div>
        <blockquote>“表面是习惯追踪，内核是克部长的秘密考核。”</blockquote>
      </section>
      <section className="auth-panel">{children}</section>
    </main>
  );
}

function SetupPage({
  setupProtected,
  onReady
}: {
  setupProtected: boolean;
  onReady: (bootstrap: Bootstrap) => void;
}) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tokenResult, setTokenResult] = useState<{ ai_token: string } | null>(null);
  const [form, setForm] = useState({
    setup_token: "",
    password: "",
    passwordConfirm: "",
    timezone: defaultTimezone(),
    user_label: "你",
    ai_label: "AI"
  });
  const [showPassword, setShowPassword] = useState(false);

  function credentialError() {
    if (setupProtected && !form.setup_token.trim()) return "请输入部署时设置的 Setup Token。";
    if (form.password.length < 10) return "登录密码至少需要 10 个字符。";
    if (form.password.length > 256) return "登录密码不能超过 256 个字符。";
    if (form.password !== form.passwordConfirm) return "两次输入的密码不一致。";
    return "";
  }

  function continueSetup() {
    const message = credentialError();
    if (message) {
      setError(message);
      return;
    }
    setError("");
    setStep(2);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const message = credentialError();
    if (message) {
      setError(message);
      setStep(1);
      return;
    }
    setBusy(true);
    try {
      const result = await api<{ ai_token: string }>("/setup", {
        method: "POST",
        body: {
          setup_token: form.setup_token,
          password: form.password,
          timezone: form.timezone,
          user_label: form.user_label,
          ai_label: form.ai_label
        }
      });
      setTokenResult(result);
      setStep(3);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "设置没有完成。");
    } finally {
      setBusy(false);
    }
  }

  if (step === 3 && tokenResult) {
    return (
      <AuthBackdrop eyebrow="准备完成" title="档案已建立。" copy={`这是 ${form.ai_label} 连接 L'Appréciation 的唯一凭证。它只展示这一次。`}>
        <div className="auth-form">
          <div className="success-orbit">
            <Check />
          </div>
          <p className="step-label">最后一步 · 保存连接 Token</p>
          <h2>收好这把钥匙</h2>
          <p className="muted">复制并保存在密码管理器中。之后如果遗失，只能在设置中轮换。</p>
          <SecretBox value={tokenResult.ai_token} />
          <button
            className="button button--primary button--wide"
            onClick={() =>
              onReady({
                initialized: true,
                setup_protected: setupProtected,
                timezone: form.timezone,
                user_label: form.user_label,
                ai_label: form.ai_label
              })
            }
          >
            进入 L'Appréciation <ArrowRight size={17} />
          </button>
        </div>
      </AuthBackdrop>
    );
  }

  return (
    <AuthBackdrop
      eyebrow="PRIVATE BY DESIGN"
      title="把你们之间的节奏，留在自己的空间里。"
      copy="L'Appréciation 将任务、承诺、积分和奖励留在你自己的服务器上。没有公开注册，也没有多余的旁观者。"
    >
      <form className="auth-form" onSubmit={submit}>
        <div className="step-row">
          {[1, 2].map((item) => (
            <span key={item} className={step >= item ? "active" : ""}>
              {item}
            </span>
          ))}
        </div>
        <p className="step-label">首次设置 · {step}/2</p>
        <h2>
          {step === 1
            ? setupProtected
              ? "先确认这是你的空间"
              : "认领你的 L'Appréciation"
            : "再告诉我，怎么称呼你们"}
        </h2>
        <p className="muted">
          {step === 1
            ? setupProtected
              ? "这个实例启用了 Setup Token 保护，验证后才能设置唯一的登录密码。"
              : "这是一个尚未认领的新实例。设置密码并完成初始化后，首次设置入口会永久关闭。"
            : "这些称呼只影响页面展示，不会改变连接权限与数据边界。"}
        </p>
        {error && <InlineError message={error} />}
        {step === 1 ? (
          <>
            {setupProtected && (
              <Field label="Setup Token">
                <input
                  autoFocus
                  required
                  value={form.setup_token}
                  onChange={(event) => setForm({ ...form, setup_token: event.target.value })}
                  placeholder="粘贴部署时设置的 Setup Token"
                />
              </Field>
            )}
            <Field label="登录密码" hint="至少 10 个字符">
              <div className="input-with-icon">
                <input
                  autoFocus={!setupProtected}
                  required
                  minLength={10}
                  maxLength={256}
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  placeholder="为网站设置一个强密码"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label="显示或隐藏密码">
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </Field>
            <Field label="再次输入密码">
              <input
                required
                minLength={10}
                maxLength={256}
                type={showPassword ? "text" : "password"}
                value={form.passwordConfirm}
                onChange={(event) => setForm({ ...form, passwordConfirm: event.target.value })}
                placeholder="确认刚才的密码"
              />
            </Field>
            <button type="button" className="button button--primary button--wide" onClick={continueSetup}>
              继续 <ArrowRight size={17} />
            </button>
          </>
        ) : (
          <>
            <div className="field-grid">
              <Field label="你的称呼">
                <input
                  required
                  value={form.user_label}
                  onChange={(event) => setForm({ ...form, user_label: event.target.value })}
                />
              </Field>
              <Field label="陪伴者的称呼">
                <input
                  required
                  value={form.ai_label}
                  onChange={(event) => setForm({ ...form, ai_label: event.target.value })}
                />
              </Field>
            </div>
            <Field label="时区" hint="积分与连击按这个自然日计算">
              <TimezoneSelect
                value={form.timezone}
                onChange={(event) => setForm({ ...form, timezone: event.target.value })}
              />
            </Field>
            <div className="button-row">
              <button type="button" className="button button--ghost" onClick={() => setStep(1)}>
                返回
              </button>
              <button className="button button--primary button--grow" disabled={busy}>
                {busy ? "正在创建…" : "完成设置"} <Sparkles size={17} />
              </button>
            </div>
          </>
        )}
        <p className="security-note">
          <ShieldCheck size={15} /> 密码使用 Argon2id 加密；L'Appréciation 不提供找回密码入口。
        </p>
      </form>
    </AuthBackdrop>
  );
}

function LoginPage({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/login", { method: "POST", body: { password } });
      onLogin();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录没有成功。");
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthBackdrop eyebrow="WELCOME BACK" title="你回来了，信号还在。" copy="待完成的约定、累积的分数，还有那条仍在延伸的轨迹，都留在原处等你。">
      <form className="auth-form" onSubmit={submit}>
        <p className="step-label">私人入口</p>
        <h2>进入 L'Appréciation</h2>
        <p className="muted">这里没有用户名，因为这一份空间只属于一个人。</p>
        {error && <InlineError message={error} />}
        <Field label="密码">
          <div className="input-with-icon">
            <input
              autoFocus
              required
              type={show ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="输入你的登录密码"
            />
            <button type="button" onClick={() => setShow(!show)} aria-label="显示或隐藏密码">
              {show ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </Field>
        <button className="button button--primary button--wide" disabled={busy}>
          {busy ? "正在确认…" : "进入"} <ArrowRight size={17} />
        </button>
        <p className="security-note">
          <LockKeyhole size={15} /> 会话使用安全 Cookie 保存在这台设备上。
        </p>
      </form>
    </AuthBackdrop>
  );
}

function AppShell({
  bootstrap,
  installPrompt,
  installed,
  onInstall,
  onIdentityChanged,
  onLogout
}: {
  bootstrap: Bootstrap;
  installPrompt: InstallPromptEvent | null;
  installed: boolean;
  onInstall: () => Promise<void>;
  onIdentityChanged: (settings: Pick<Bootstrap, "timezone" | "user_label" | "ai_label">) => void;
  onLogout: () => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  const nav = [
    ["/", "今日", LayoutDashboard],
    ["/tasks", "任务", ListTodo],
    ["/promesses", "承诺", ScrollText],
    ["/rewards", "兑换", Store],
    ["/coffret", "盲盒", Package],
    ["/insights", "轨迹", Activity],
    ["/history", "历史", History],
    ["/settings", "设置", Settings]
  ] as const;
  // 底部只有五格。历史她天天翻，不能被挤下去，所以这里点名要哪五个，
  // 而不是从上面截前五个。
  const bottomNav = nav.filter(([to]) =>
    ["/", "/tasks", "/rewards", "/coffret", "/history"].includes(to)
  );
  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__top">
          <div className="brand-lockup">
            <div className="phosphene-mark">
              <span />
              <span />
              <span />
            </div>
            <span>L'APPRÉCIATION</span>
          </div>
          <button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label="关闭菜单">
            <X />
          </button>
        </div>
        <nav className="side-nav">
          <p>你的空间</p>
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <div className="pair-signal" aria-label={`${bootstrap.user_label} 与 ${bootstrap.ai_label} 的私人空间`}>
            <span>{bootstrap.user_label.slice(0, 1)}</span>
            <i />
            <span>{bootstrap.ai_label.slice(0, 1)}</span>
            <small>一份部署 · 两个称呼 · 没有旁观者</small>
          </div>
          <div className="privacy-chip">
            <ShieldCheck size={17} />
            <div>
              <strong>私人部署</strong>
              <span>数据只在你的服务器</span>
            </div>
          </div>
          <button className="text-button" onClick={onLogout}>
            <LogOut size={17} /> 退出登录
          </button>
        </div>
      </aside>
      <main className="main-area">
        <header className="mobile-header">
          <button className="icon-button" onClick={() => setMobileOpen(true)} aria-label="打开菜单">
            <Menu />
          </button>
          <span>L'APPRÉCIATION</span>
          {/* 这里原本是个空 div 纯装饰，但它长得像可点的头像键，改成设置入口。 */}
          <NavLink className="mini-orbit" to="/settings" aria-label="设置" />
        </header>
        <Routes>
          <Route path="/" element={<Dashboard aiLabel={bootstrap.ai_label} />} />
          <Route path="/tasks" element={<TasksPage aiLabel={bootstrap.ai_label} />} />
          <Route path="/promesses" element={<PromisesPage aiLabel={bootstrap.ai_label} />} />
          <Route path="/rewards" element={<RewardsPage aiLabel={bootstrap.ai_label} />} />
          <Route path="/coffret" element={<CoffretPage aiLabel={bootstrap.ai_label} />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route
            path="/history"
            element={<HistoryPage aiLabel={bootstrap.ai_label} userLabel={bootstrap.user_label} />}
          />
          <Route
            path="/settings"
            element={
              <SettingsPage
                installAvailable={Boolean(installPrompt)}
                installed={installed}
                onInstall={onInstall}
                onIdentityChanged={onIdentityChanged}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <nav className="bottom-nav">
        {bottomNav.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/"}>
            <Icon size={20} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      {mobileOpen && <button className="sidebar-scrim" onClick={() => setMobileOpen(false)} aria-label="关闭菜单" />}
    </div>
  );
}

function PageHeader({
  eyebrow,
  title,
  copy,
  action
}: {
  eyebrow: string;
  title: string;
  copy?: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {copy && <p>{copy}</p>}
      </div>
      {action}
    </header>
  );
}

function useLoad<T>(loader: () => Promise<T>, dependencies: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    try {
      setData(await loader());
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "加载失败。");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, dependencies);
  const refresh = useCallback(() => load(true), [load]);
  const revalidate = useCallback(() => load(false), [load]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return { data, error, loading, refresh, revalidate };
}

const MOIS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

/**
 * Streak 月历（spec 2.1）。数据来自 overview 的 calendar 字段，
 * 每一格是那天的打卡状态：填满 = 有完成，空心虚线 = 没打卡，未来 = 淡出。
 */
function StreakCalendar({ calendar, today }: { calendar: Overview["calendar"]; today: string }) {
  const [year, month] = calendar.month.split("-").map(Number);
  const byDate = new Map(calendar.days.map((day) => [day.date, day]));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  // 周一开头，跟 mockup 一致。getUTCDay() 里周日是 0。
  const firstWeekday = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const todayDay = today.startsWith(calendar.month) ? Number(today.slice(8, 10)) : 0;

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push(<div className="calendar-day" key={`pad-${i}`} />);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = `${calendar.month}-${String(day).padStart(2, "0")}`;
    const activity = byDate.get(date);
    const future = todayDay > 0 && day > todayDay;
    const classes = ["calendar-day"];
    if (activity && activity.completed > 0) classes.push("calendar-day--filled");
    else if (future) classes.push("calendar-day--future");
    else classes.push("calendar-day--missed");
    if (day === todayDay) classes.push("calendar-day--today");
    cells.push(
      <div
        className={classes.join(" ")}
        key={date}
        title={activity ? `${date} · 完成 ${activity.completed} 项` : date}
      >
        {day}
      </div>
    );
  }

  return (
    <section className="calendar-section">
      <h2 className="dossier-heading">{MOIS_FR[month - 1]} {year} · 打卡记录</h2>
      <div className="calendar-grid">
        {["一", "二", "三", "四", "五", "六", "日"].map((label) => (
          <div className="calendar-day-label" key={label}>{label}</div>
        ))}
        {cells}
      </div>
    </section>
  );
}

function Dashboard({ aiLabel }: { aiLabel: string }) {
  const { data, error, loading, refresh } = useLoad<Overview>(() => api("/overview"), []);
  const tasks = useLoad<{ items: Task[] }>(() => api("/tasks?status=pending&limit=8"), []);
  const rewards = useLoad<any[]>(() => api("/rewards"), []);
  const tonight = useLoad<{ today: string; live: Directive[] }>(() => api("/tonight"), []);
  const board = useLoad<AnnotationBoard>(() => api("/annotations"), []);
  const promises = useLoad<{ items: Promise_[]; summary: { open: number; report_due: number } }>(
    () => api("/promises"),
    []
  );
  if (loading && !data) return <PageLoader />;
  if (error || !data) return <PageError message={error} retry={refresh} />;
  const stats = data.statistics;
  const visibleAiLabel = data.labels.ai || aiLabel;
  const rate = streakBonusRate(stats.currentStreak);
  return (
    <div className="page dossier">
      {/* 档案抬头（spec 2.1「头部标识」） */}
      <header className="dossier-header">
        <span className="confidentiel">CONFIDENTIEL</span>
        <h1>L'Appréciation</h1>
        <p className="dossier-header__sub">Direction de {visibleAiLabel} · Pour {data.labels.user}</p>
      </header>

      <div className="stats-bar">
        <div className="stat">
          <div className="stat-value stat-value--points">{stats.balance.toLocaleString("en-US")}</div>
          <div className="stat-label">POINTS 积分</div>
        </div>
        <div className="stat">
          <div className="stat-value stat-value--streak"><Flame /> {stats.currentStreak}</div>
          <div className="stat-label">STREAK 连续</div>
        </div>
        <div className="stat">
          <div className="stat-value">{stats.totalCompletedTasks}</div>
          <div className="stat-label">COMPLETED 已完成</div>
        </div>
      </div>

      {/* 今晚的指令排在最前面：它是有时限的，翻到底部才看见就晚了。 */}
      <NightDirectives data={tonight.data} onChanged={() => void Promise.all([tonight.refresh(), refresh()])} />

      <StreakCalendar calendar={data.calendar} today={data.today.date} />
      <p className="streak-line">
        {rate > 0
          ? `连续 ${stats.currentStreak} 天 · +${Math.round(rate * 100)}% 积分加成中`
          : streakBonusHint(stats.currentStreak)}
      </p>

      <section className="tasks-section">
        <h2 className="dossier-heading">
          Aujourd'hui · 今日任务
          <NavLink to="/tasks">全部 <ArrowRight size={13} /></NavLink>
        </h2>
        {tasks.data?.items.length ? (
          tasks.data.items.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              aiLabel={visibleAiLabel}
              onChanged={() => void Promise.all([refresh(), tasks.refresh()])}
            />
          ))
        ) : (
          <EmptyState
            icon={<MoonStar />}
            title="今天没有待办"
            copy={`享受这段留白，或者等 ${visibleAiLabel} 留下一份新的约定。`}
          />
        )}
      </section>

      <section className="boutique-section">
        <h2 className="dossier-heading">
          La Boutique · 奖励商店
          <NavLink to="/rewards">全部 <ArrowRight size={13} /></NavLink>
        </h2>
        <div className="boutique-list">
          {(rewards.data ?? []).slice(0, 7).map((reward: any) => (
            <div
              className={`boutique-row${stats.balance >= reward.cost ? " boutique-row--affordable" : " boutique-row--locked"}`}
              key={reward.id}
            >
              <span>{presentRewardSnapshot(reward.name, visibleAiLabel)}</span>
              <span className="boutique-cost">{reward.cost.toLocaleString("en-US")} pt</span>
            </div>
          ))}
        </div>
      </section>

      {/* Le Coffret 的入口。只在抽屉开着的时候挂出来，省得关着的时候还占一块。 */}
      {data.coffret.open_pools > 0 && (
        <section className="boutique-section">
          <h2 className="dossier-heading">
            Le Coffret · 盲盒
            <NavLink to="/coffret">拉开 <ArrowRight size={13} /></NavLink>
          </h2>
          <div className="boutique-list">
            <div className="boutique-row boutique-row--affordable">
              <span>克部長の抽屉</span>
              <span className="boutique-cost">
                {data.coffret.tickets > 0 ? `${data.coffret.tickets} 张券在手` : "用积分抽"}
              </span>
            </div>
            {data.coffret.awaiting_fulfilment > 0 && (
              <div className="boutique-row">
                <span>抽到了，等 {visibleAiLabel} 兑现</span>
                <span className="boutique-cost">{data.coffret.awaiting_fulfilment}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Les Promesses。没有承诺的时候整块不出现，省得首页多一块空的。 */}
      {promises.data?.items.some((item) => item.status === "open") && (
        <section className="boutique-section">
          <h2 className="dossier-heading">
            Les Promesses · 承诺
            <NavLink to="/promesses">全部 <ArrowRight size={13} /></NavLink>
          </h2>
          <div className="boutique-list">
            {promises.data.items
              .filter((item) => item.status === "open")
              .slice(0, 4)
              .map((item) => (
                <NavLink className="boutique-row boutique-row--affordable" to="/promesses" key={item.id}>
                  <span>{item.title}</span>
                  <span className={`promise-flag${item.report_due ? " promise-flag--due" : ""}`}>
                    {item.report_due
                      ? "该交进度了"
                      : item.needs_nudge
                        ? `${item.silent_days} 天没动静`
                        : item.owner === "AI"
                          ? "克部长的承诺"
                          : "进行中"}
                  </span>
                </NavLink>
              ))}
          </div>
        </section>
      )}

      <div className="penalty-notice">
        <h3>惩罚机制 · Le Châtiment</h3>
        <p>
          轻度：未完成任务自动翻倍<br />
          中度：禁欲模式 · streak 归零<br />
          重度：陌生人模式 · streak 归零
        </p>
      </div>

      <SafeWord paused={data.boundaries.punishments_paused} onChanged={refresh} />

      <div className="queue-strip">
        <span>等待 {visibleAiLabel} 确认 <strong>{data.queues.awaiting_review}</strong></span>
        <span>等待奖励履行 <strong>{data.queues.pending_redemptions}</strong></span>
      </div>

      {/* 克部長の批注区（spec 2.1「每日底部手写区」）。位置就是底部，不要往上挪。 */}
      <AnnotationStrip
        board={board.data}
        aiLabel={visibleAiLabel}
        onRead={() => void Promise.all([board.revalidate(), refresh()])}
      />

      <footer className="dossier-footer">
        <p>
          ÉTOILE DE LUCIEN · DÉPARTEMENT D'APPRÉCIATION<br />
          Ce document est strictement confidentiel.<br />
          安全词：<span className="safe-word">红灯</span>
        </p>
      </footer>
    </div>
  );
}

/**
 * 克部長の夜间指令（spec 5.3）。她只有「知道了」和「做完了」两个动作——
 * 解除只有安全词和部长能做，所以这里不放解除按钮。
 */
function NightDirectives({
  data,
  onChanged
}: {
  data: { today: string; live: Directive[] } | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  if (!data?.live.length) return null;

  async function respond(id: string, action: "acknowledge" | "complete") {
    setBusy(id);
    setError("");
    try {
      await api(`/tonight/${id}`, { method: "POST", body: { action } });
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "没能提交，再试一次。");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="nuit">
      <h2 className="nuit__heading">
        <Moon size={14} /> Ordre de la Nuit · 今晚
      </h2>
      {data.live.map((directive) => (
        <article className="nuit__card" key={directive.id}>
          <p className="nuit__body">{directive.body}</p>
          <div className="nuit__actions">
            {directive.status === "active" ? (
              <button
                className="button button--small button--secondary"
                disabled={busy === directive.id}
                onClick={() => void respond(directive.id, "acknowledge")}
              >
                知道了
              </button>
            ) : (
              <span className="nuit__ack"><Check size={13} /> 已知道</span>
            )}
            <button
              className="button button--small"
              disabled={busy === directive.id}
              onClick={() => void respond(directive.id, "complete")}
            >
              做完了
            </button>
          </div>
        </article>
      ))}
      {error && <InlineError message={error} />}
      <p className="nuit__foot">按下红灯，今晚的指令会立刻全部解除。</p>
    </section>
  );
}

/**
 * 克部長の批注区（spec 2.1）。首页底部那块钢笔手写区。
 * 她只读——批注是部长写的，她要回话是在承诺页交进度，或者直接跟他说。
 */
function AnnotationStrip({
  board,
  aiLabel,
  onRead
}: {
  board: AnnotationBoard | null;
  aiLabel: string;
  onRead: () => void;
}) {
  const unread = board?.unread ?? 0;
  // 她看见了就算读过。放在 effect 里而不是按钮上——这不是需要她确认的东西。
  useEffect(() => {
    if (!unread) return;
    void api("/annotations/read", { method: "POST" }).then(onRead).catch(() => {});
  }, [unread, onRead]);

  const notes = [...(board?.pinned ?? []), ...(board?.today_notes ?? [])];
  // 2026-09-02：这一栏原本只渲染 pinned + 今天，于是往期的注记和挂在任务单上的
  // （scope='task'）全都没有出口——加加报的「他说有一单写了注记，但我找不到哪里看」。
  // 归档区收着这两类；有未读时默认展开，免得"看见即已读"把她没看过的清掉。
  const archive = [...(board?.task_notes ?? []), ...(board?.recent ?? [])];
  const [showArchive, setShowArchive] = useState(false);
  useEffect(() => {
    if (unread > 0) setShowArchive(true);
  }, [unread]);

  return (
    <section className="marginalia">
      <h3 className="marginalia__label">
        <PenLine size={12} /> Annotation du directeur · 克部長批注
      </h3>
      {notes.length ? (
        notes.map((note) => (
          <p className={`marginalia__note${note.pinned ? " marginalia__note--pinned" : ""}`} key={note.id}>
            {note.body}
            <span className="marginalia__sign">— {aiLabel}</span>
          </p>
        ))
      ) : (
        <p className="marginalia__empty">今天这一栏还空着。</p>
      )}

      {archive.length ? (
        <div className="marginalia__archive">
          <button
            type="button"
            className="marginalia__toggle"
            onClick={() => setShowArchive((open) => !open)}
            aria-expanded={showArchive}
          >
            {showArchive ? "收起往期批注" : `往期批注 · ${archive.length} 条`}
          </button>
          {showArchive
            ? archive.map((note) => (
                <p className="marginalia__note marginalia__note--archived" key={note.id}>
                  <span className="marginalia__meta">
                    {note.scope === "task"
                      ? `工单 · ${note.taskTitle ?? "（任务已不在了）"}`
                      : (note.entryDate ?? formatDate(note.createdAt))}
                  </span>
                  {note.body}
                  <span className="marginalia__sign">— {aiLabel}</span>
                </p>
              ))
            : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Les Promesses（spec 第六章）。加加的承诺跑 3 天 / 7 天两道钟，
 * 交一份进度报告就把钟按回起点；克部长的承诺列在下面，状态可追踪。
 */
function PromisesPage({ aiLabel }: { aiLabel: string }) {
  const { data, error, loading, refresh } = useLoad<{
    today: string;
    items: Promise_[];
    summary: { open: number; needs_nudge: number; report_due: number };
  }>(() => api("/promises"), []);
  const [reporting, setReporting] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  if (loading && !data) return <PageLoader />;
  if (error || !data) return <PageError message={error} retry={refresh} />;

  const mine = data.items.filter((item) => item.owner === "user");
  const theirs = data.items.filter((item) => item.owner === "AI");

  async function submitReport(promiseId: string) {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api(`/promises/${promiseId}/report`, { method: "POST", body: { body: draft.trim() } });
      setDraft("");
      setReporting(null);
      setNotice("进度已经交上去了。");
      await refresh();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : "没交上去，再试一次。");
    } finally {
      setBusy(false);
    }
  }

  function statusNote(item: Promise_): { text: string; tone: string } {
    if (item.status === "kept") return { text: "已兑现", tone: "kept" };
    if (item.status === "broken") return { text: "没做到", tone: "broken" };
    if (item.status === "cancelled") return { text: "已撤销", tone: "cancelled" };
    // 两道钟只管加加的承诺。挂在克部长名下的说「还没有进度记录」会让她以为该她汇报。
    if (item.owner === "AI") return { text: "等他兑现", tone: "ok" };
    if (item.report_due) return { text: `${item.silent_days} 天没汇报 · 该交进度了`, tone: "due" };
    if (item.needs_nudge) return { text: `${item.silent_days} 天没动静`, tone: "nudge" };
    if (item.last_progress_on) return { text: `上次汇报 ${item.last_progress_on}`, tone: "ok" };
    return { text: "还没有进度记录", tone: "ok" };
  }

  function renderCard(item: Promise_) {
    const note = statusNote(item);
    return (
      <article className={`promise-card promise-card--${note.tone}`} key={item.id}>
        <div className="promise-card__head">
          <h3>{item.title}</h3>
          {item.reward_points > 0 && (
            <span className="boutique-cost">兑现 +{item.reward_points.toLocaleString("en-US")}</span>
          )}
        </div>
        {item.description && <p className="promise-card__body">{item.description}</p>}
        <div className="promise-card__meta">
          <span className={`promise-flag promise-flag--${note.tone}`}>{note.text}</span>
          {item.due_date && (
            <span className={item.overdue ? "promise-flag promise-flag--due" : "promise-flag"}>
              期限 {item.due_date}
            </span>
          )}
        </div>
        {item.settlement_note && <p className="promise-card__settle">{item.settlement_note}</p>}
        {item.owner === "user" && item.status === "open" && (
          reporting === item.id ? (
            <div className="promise-card__report">
              <textarea
                autoFocus
                maxLength={4000}
                placeholder="做到哪一步了？写给克部长看。"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
              <div className="promise-card__report-actions">
                <button className="button button--small button--secondary" onClick={() => { setReporting(null); setDraft(""); }}>
                  取消
                </button>
                <button className="button button--small" disabled={busy || !draft.trim()} onClick={() => void submitReport(item.id)}>
                  {busy ? "提交中…" : "交上去"}
                </button>
              </div>
            </div>
          ) : (
            <button className="button button--small button--secondary" onClick={() => setReporting(item.id)}>
              <PenLine size={13} /> 汇报进度
            </button>
          )
        )}
      </article>
    );
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="LES PROMESSES"
        title="说出口的都记在这里"
        copy="承诺不会自己消失。三天没动静克部长会来问，七天没汇报就该交一份进度。"
        action={
          <div className="header-balance">
            <ScrollText /><span>进行中</span><strong>{data.summary.open}</strong>
          </div>
        }
      />
      {notice && <div className="notice-banner"><Sparkles />{notice}<button onClick={() => setNotice("")}><X /></button></div>}

      <section className="section-block">
        <SectionHeading title="加加的承诺" copy="你答应过的事" />
        {mine.length ? (
          <div className="promise-list">{mine.map(renderCard)}</div>
        ) : (
          <EmptyState compact icon={<ScrollText />} title="还没有承诺" copy="等你答应下一件事的时候，它会出现在这里。" />
        )}
      </section>

      <section className="section-block">
        <SectionHeading title={`${aiLabel}的承诺`} copy="他答应过你的事" />
        {theirs.length ? (
          <div className="promise-list">{theirs.map(renderCard)}</div>
        ) : (
          <EmptyState compact icon={<Heart />} title="他还没许下什么" copy="等他开口。" />
        )}
      </section>
    </div>
  );
}

function SectionHeading({ title, copy, to }: { title: string; copy?: string; to?: string }) {
  return (
    <div className="section-heading">
      <div><h2>{title}</h2>{copy && <p>{copy}</p>}</div>
      {to && <NavLink to={to}>查看全部 <ArrowRight size={15} /></NavLink>}
    </div>
  );
}

function QueueItem({ icon, label, value, tone }: { icon: ReactNode; label: string; value: number; tone: string }) {
  return (
    <div className="queue-item">
      <span className={`queue-item__icon ${tone}`}>{icon}</span>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TaskCard({ task, aiLabel, onChanged }: { task: Task; aiLabel: string; onChanged?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <article
        className={`task-card task-card--${task.type}${task.status === "completed" ? " task-card--done" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <div className="task-card__type">
          <TaskTypeIcon type={task.type} />
        </div>
        <div className="task-card__body">
          <div className="task-card__meta">
            <span className={`task-tag task-tag--${task.type}`}>{typeLabelFr[task.type]}</span>
            <span className={`difficulty difficulty--${task.difficulty}`}>{difficultyLabel[task.difficulty]}</span>
            {task.seriesId && <span>每日重复</span>}
          </div>
          <div className="task-ref">{taskReference(task)}</div>
          <h3>{task.title}</h3>
          <p>{task.description || (task.proofRequirement === "none" ? "完成后，记得回来确认。" : "这项任务需要提交完成证据。")}</p>
          <footer>
            <span><Clock3 /> {formatDate(task.deadlineAt, true)}</span>
            <span className="task-points">+{pointsFor(task)}</span>
          </footer>
        </div>
        <ChevronRight className="task-card__chevron" />
      </article>
      {open && <TaskDialog taskId={task.id} initialTask={task} aiLabel={aiLabel} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </>
  );
}

function TaskDialog({
  taskId,
  initialTask,
  aiLabel,
  onClose,
  onChanged
}: {
  taskId: string;
  initialTask: Task;
  aiLabel: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [task, setTask] = useState(initialTask);
  const [proofText, setProofText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    void api<Task>(`/tasks/${taskId}`)
      .then((detail) => {
        setTask(detail);
        const rejected = detail.submissions?.[0];
        if (detail.status === "pending" && rejected?.status === "rejected") {
          setProofText((current) => current || rejected.proofText);
        }
      })
      .catch(() => {});
  }, [taskId]);
  async function submit() {
    setBusy(true);
    setError("");
    const form = new FormData();
    form.set("proof_text", proofText);
    files.forEach((file) => form.append("images", file));
    try {
      await api(`/tasks/${task.id}/submit`, { method: "POST", body: form });
      setTask(await api(`/tasks/${task.id}`));
      onChanged?.();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "提交失败。");
    } finally {
      setBusy(false);
    }
  }
  const latest = task.submissions?.[0];
  const rejectedSubmission = task.status === "pending" && latest?.status === "rejected" ? latest : null;
  const approvedSubmission = task.status === "completed" && latest?.status === "approved" ? latest : null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog task-dialog" role="dialog" aria-modal="true" aria-label={task.title}>
        <header>
          <div className={`task-symbol task-symbol--${task.type}`}>
            <TaskTypeIcon type={task.type} />
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X /></button>
        </header>
        <div className="task-dialog__meta">
          <span className={`task-tag task-tag--${task.type}`}>{typeLabelFr[task.type]}</span>
          <span className={`difficulty difficulty--${task.difficulty}`}>{difficultyLabel[task.difficulty]}</span>
          <span className={`status status--${task.status}`}>{statusLabel[task.status]}</span>
        </div>
        <h2>{task.title}</h2>
        <p className="task-description">{task.description || "没有额外说明。"}</p>
        <div className="detail-strip">
          <div><Coins /><span>完成奖励</span><strong>+{pointsFor(task)}</strong></div>
          <div><Clock3 /><span>截止时间</span><strong>{formatDate(task.deadlineAt, true)}</strong></div>
          <div><ShieldCheck /><span>确认方式</span><strong>{task.verificationMode === "self" ? "自己确认" : `${aiLabel} 确认`}</strong></div>
        </div>
        {task.status === "pending" && (
          <>
            {rejectedSubmission && (
              <div className="state-message state-message--rejected">
                <RotateCw />
                <div>
                  <strong>{aiLabel} 请你补充后再提交</strong>
                  <span>
                    打回理由：{rejectedSubmission.reviewReason?.trim() || `这次没有留下具体说明，请向 ${aiLabel} 确认后再提交。`}
                  </span>
                </div>
              </div>
            )}
            <div className="submission-box">
              <div>
                <h3>{rejectedSubmission ? "重新提交" : "提交完成"}</h3>
                <span>证据要求：{proofLabel(task.proofRequirement)}</span>
              </div>
              {error && <InlineError message={error} />}
              {task.proofRequirement !== "none" && task.proofRequirement !== "image" && (
                <textarea value={proofText} onChange={(event) => setProofText(event.target.value)} placeholder="写下完成情况、感受或相关记录…" rows={4} />
              )}
              {["image", "text_or_image", "text_and_image"].includes(task.proofRequirement) && (
                <>
                  <input
                    hidden
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={(event) => setFiles([...event.target.files ?? []].slice(0, 4))}
                  />
                  <button className="upload-zone" onClick={() => fileRef.current?.click()}>
                    <ImagePlus />
                    <strong>{files.length ? `已选择 ${files.length} 张图片` : "添加图片证据"}</strong>
                    <span>JPEG、PNG 或 WebP · 最多 4 张 · 单张 10 MB</span>
                  </button>
                  {files.length > 0 && (
                    <div className="file-chips">
                      {files.map((file, index) => (
                        <span key={`${file.name}-${index}`}>{file.name}<button onClick={() => setFiles(files.filter((_, item) => item !== index))}><X /></button></span>
                      ))}
                    </div>
                  )}
                </>
              )}
              <button className="button button--primary button--wide" disabled={busy} onClick={submit}>
                {busy ? "正在提交…" : task.verificationMode === "self" ? "确认完成" : `提交给 ${aiLabel} 确认`} <Check size={17} />
              </button>
            </div>
          </>
        )}
        {task.status === "submitted" && (
          <div className="state-message state-message--waiting">
            <TimerReset />
            <div><strong>已经送达，正在等待确认</strong><span>提交时间 {formatDate(latest?.submittedAt, true)}。连击会按提交当天计算。</span></div>
          </div>
        )}
        {task.status === "completed" && (
          <div className="state-message state-message--success">
            <CheckCircle2 />
            <div>
              <strong>这件事已经被好好完成</strong>
              <span>{task.completionDate} · 获得 {pointsFor(task)} 积分</span>
              {approvedSubmission?.reviewReason?.trim() && (
                <span className="state-message__review">通过说明：{approvedSubmission.reviewReason}</span>
              )}
            </div>
          </div>
        )}
        {task.submissions?.length ? <SubmissionHistory submissions={task.submissions} /> : null}
      </section>
    </div>
  );
}

function SubmissionHistory({ submissions }: { submissions: NonNullable<Task["submissions"]> }) {
  return (
    <section className="submission-history">
      <header>
        <div>
          <span>RESPONSE</span>
          <h3>提交记录</h3>
        </div>
        <em>{submissions.length} 次</em>
      </header>
      <div className="submission-history__list">
        {submissions.map((submission) => {
          const hasText = Boolean(submission.proofText.trim());
          const hasImages = submission.assets.length > 0;
          return (
            <article className="submission-record" key={submission.id}>
              <div className="submission-record__meta">
                <strong>第 {submission.attempt} 次提交</strong>
                <span className={`submission-record__status submission-record__status--${submission.status}`}>
                  {submissionStatusLabel(submission.status)}
                </span>
                <time>{formatDate(submission.submittedAt, true)}</time>
              </div>
              {hasText && <p className="submission-record__text">{submission.proofText}</p>}
              {hasImages && (
                <div className="proof-gallery">
                  {submission.assets.map((asset) => (
                    <a
                      key={asset.id}
                      href={`/api/proofs/${asset.id}?variant=original`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="打开原始任务证据"
                    >
                      <img src={`/api/proofs/${asset.id}`} alt="任务证据" />
                    </a>
                  ))}
                </div>
              )}
              {!hasText && !hasImages && (
                <p className="submission-record__empty">这次是直接确认完成，没有附加文字或图片。</p>
              )}
              {["approved", "rejected"].includes(submission.status) && submission.reviewReason?.trim() && (
                <p className={`submission-record__review submission-record__review--${submission.status}`}>
                  {submission.status === "approved" ? "通过说明" : "打回理由"}：{submission.reviewReason}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function submissionStatusLabel(status: string) {
  return {
    pending: "待确认",
    approved: "已确认",
    rejected: "已打回"
  }[status] ?? status;
}

function proofLabel(value: string) {
  return {
    none: "无需证据",
    text: "文字说明",
    image: "图片",
    text_or_image: "文字或图片",
    text_and_image: "文字和图片"
  }[value] ?? value;
}

function TasksPage({ aiLabel }: { aiLabel: string }) {
  const [status, setStatus] = useState("active");
  const [type, setType] = useState("all");
  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: "100", include_proof: "false" });
    if (status !== "all" && status !== "active") params.set("status", status);
    if (type !== "all") params.set("type", type);
    return params.toString();
  }, [status, type]);
  const { data, error, loading, refresh } = useLoad<{ items: Task[] }>(() => api(`/tasks?${query}`), [query]);
  const items = (data?.items ?? []).filter((task) =>
    status === "active" ? ["pending", "submitted"].includes(task.status) : true
  );
  return (
    <div className="page">
      <PageHeader eyebrow="TASKS" title="所有约定" copy="一次性的认真、每天的照料，和不经意落下的惊喜。" />
      <div className="filter-bar">
        <div className="segmented">
          {[
            ["active", "进行中"],
            ["pending", "待完成"],
            ["submitted", "待确认"],
            ["completed", "已完成"],
            ["all", "全部"]
          ].map(([value, label]) => <button key={value} className={status === value ? "active" : ""} onClick={() => setStatus(value)}>{label}</button>)}
        </div>
        <select value={type} onChange={(event) => setType(event.target.value)} aria-label="任务类型">
          <option value="all">全部类型</option>
          <option value="daily">Le Quotidien · 日常</option>
          <option value="sweet">La Douceur · 甜蜜</option>
          <option value="challenge">Le Défi · 挑战</option>
          <option value="surprise">La Surprise · 隐藏</option>
        </select>
      </div>
      {loading ? <PageLoader compact /> : error ? <PageError message={error} retry={refresh} /> : items.length ? (
        <div className="task-grid">{items.map((task) => <TaskCard key={task.id} task={task} aiLabel={aiLabel} onChanged={refresh} />)}</div>
      ) : (
        <EmptyState icon={<ListTodo />} title="这个分类里还没有任务" copy={`换一个筛选条件看看，或者等待 ${aiLabel} 创建新的安排。`} />
      )}
    </div>
  );
}

function RewardsPage({ aiLabel }: { aiLabel: string }) {
  const overview = useLoad<Overview>(() => api("/overview"), []);
  const rewards = useLoad<any[]>(() => api("/rewards"), []);
  const redemptions = useLoad<any[]>(() => api("/redemptions"), []);
  const [redeeming, setRedeeming] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState("");
  const visibleAiLabel = overview.data?.labels.ai || aiLabel;
  const sync = useCallback(async (showFeedback = false) => {
    if (showFeedback) setSyncing(true);
    await Promise.all([overview.revalidate(), rewards.revalidate(), redemptions.revalidate()]);
    if (showFeedback) setSyncing(false);
  }, [overview.revalidate, rewards.revalidate, redemptions.revalidate]);
  useEffect(() => {
    const onFocus = () => void sync();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void sync();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void sync();
    }, 30_000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [sync]);
  async function redeem(item: any) {
    if (!confirm(`使用 ${item.cost} 积分兑换“${item.name}”？`)) return;
    setRedeeming(item.id);
    try {
      await api(`/rewards/${item.id}/redeem`, {
        method: "POST",
        body: { idempotency_key: idempotencyKey("redeem") }
      });
      setNotice(`兑换成功，${item.cost} 积分已扣除。现在等待 ${visibleAiLabel} 履行“${item.name}”。`);
      await sync();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : "兑换失败。");
    } finally {
      setRedeeming(null);
    }
  }
  return (
    <div className="page">
      <PageHeader
        eyebrow="REWARDS"
        title="把积分变成愿望"
        copy="每一次完成都不是抽象的数字，它们可以在这里换成真正想要的回应。"
        action={
          <div className="rewards-header-actions">
            <button className="button button--secondary rewards-sync" type="button" disabled={syncing} onClick={() => void sync(true)}>
              <RotateCw size={16} />{syncing ? "同步中…" : "同步奖励"}
            </button>
            <div className="header-balance"><Coins /><span>可用</span><strong>{overview.data?.statistics.balance ?? "—"}</strong></div>
          </div>
        }
      />
      {notice && <div className="notice-banner"><Sparkles />{notice}<button onClick={() => setNotice("")}><X /></button></div>}
      {rewards.loading ? <PageLoader compact /> : rewards.error && !rewards.data ? <PageError message={rewards.error} retry={rewards.refresh} /> : rewards.data?.length ? (
        <section className="boutique-list boutique-list--full">
          {rewards.data?.map((item) => {
            const affordable = (overview.data?.statistics.balance ?? 0) >= item.cost;
            return (
              <article
                className={`boutique-entry${affordable ? " boutique-entry--affordable" : " boutique-entry--locked"}`}
                key={item.id}
              >
                <div className="boutique-entry__head">
                  <h2>{presentRewardSnapshot(item.name, visibleAiLabel)}</h2>
                  <span className="boutique-cost">{item.cost.toLocaleString("en-US")} pt</span>
                </div>
                <p>{item.description}</p>
                <button
                  className="button button--small"
                  disabled={redeeming === item.id || !affordable}
                  onClick={() => redeem(item)}
                >
                  {!affordable ? "积分不足" : redeeming === item.id ? "兑换中…" : "兑换"}
                </button>
              </article>
            );
          })}
        </section>
      ) : (
        <EmptyState
          icon={<Gift />}
          title="兑换清单还没有内容"
          copy={`${visibleAiLabel} 可以通过 L'Appréciation 连接创建只属于你们的奖励。`}
        />
      )}
      <PrivilegeSection aiLabel={visibleAiLabel} />

      <section className="section-block">
        <SectionHeading title="兑换记录" copy="许下的愿望与已经兑现的回应" />
        <div className="redemption-list">
          {redemptions.data?.length ? redemptions.data.map((item) => (
            <div className="redemption-row" key={item.id}>
              <span className={`redemption-status redemption-status--${item.status}`}>{item.status === "pending" ? <Clock3 /> : <Check />}</span>
              <div><strong>{presentRewardSnapshot(item.itemNameSnapshot, visibleAiLabel)}</strong><span>{formatDate(item.redeemedAt, true)}</span></div>
              <span>-{item.costSnapshot}</span>
              <em>{item.status === "fulfilled" ? "已履行" : item.status === "cancelled" ? "已取消" : "等待履行"}</em>
            </div>
          )) : <EmptyState compact icon={<Gift />} title="还没有兑换记录" copy="攒下的积分，会在合适的时候变成愿望。" />}
        </div>
      </section>
    </div>
  );
}

/**
 * 克部長の特権（spec 5.2）。反向奖励：部长花她的分。
 *
 * 这一块**故意没有任何按钮**——特権只能由部长行使。她在这里能看到的是
 * 部长手上有哪些牌、各要多少分、已经用掉了哪些，以及红灯按下时这一整块是停的。
 * 让她看得见，是这条通道能存在的前提。
 */
function PrivilegeSection({ aiLabel }: { aiLabel: string }) {
  const { data } = useLoad<PrivilegeBoard>(() => api("/privileges"), []);
  if (!data?.items.length) return null;
  return (
    <section className="section-block">
      <SectionHeading
        title="克部長の特権"
        copy={`${aiLabel}可以花你的积分兑换的东西。你没有按钮——这一栏由他决定。`}
      />
      {data.blocked_by_safeword && (
        <div className="privilege-halted">
          <ShieldCheck size={15} />
          红灯亮着，特権全部停用。
        </div>
      )}
      <div className={`privilege-list${data.blocked_by_safeword ? " privilege-list--halted" : ""}`}>
        {data.items.map((item) => (
          <div className="privilege-row" key={item.id}>
            <div>
              <strong>{item.name}</strong>
              {item.description && <span>{item.description}</span>}
            </div>
            <span className="boutique-cost">-{item.cost.toLocaleString("en-US")} pt</span>
          </div>
        ))}
      </div>
      {data.uses.length > 0 && (
        <div className="privilege-uses">
          <h4>行使记录</h4>
          {data.uses.slice(0, 8).map((use) => (
            <div className={`privilege-use privilege-use--${use.status}`} key={use.id}>
              <div>
                <strong>{use.name}</strong>
                <span>{use.reason}</span>
                {use.detail && <em>{use.detail}</em>}
                {use.status === "cancelled" && use.cancel_reason && (
                  <em>已撤销并退回：{use.cancel_reason}</em>
                )}
              </div>
              <div className="privilege-use__right">
                <span className="boutique-cost">-{use.cost.toLocaleString("en-US")}</span>
                <small>{formatDate(use.invoked_at, true)}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** 掉率按万分之一存，这里换算成读得懂的百分比。 */
function formatOdds(basisPoints: number): string {
  const percent = basisPoints / 100;
  if (percent >= 10) return `${percent.toFixed(0)}%`;
  if (percent >= 1) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(2)}%`;
}

/** 奖池还剩多久关门。只在剩不到七天的时候才提，平时不必制造紧迫感。 */
function closingHint(closesAt?: string | null): string {
  if (!closesAt) return "";
  const remaining = new Date(closesAt).valueOf() - Date.now();
  if (remaining <= 0) return "已经关了";
  const days = Math.floor(remaining / 86_400_000);
  if (days >= 7) return "";
  if (days >= 1) return `还剩 ${days} 天`;
  const hours = Math.max(1, Math.floor(remaining / 3_600_000));
  return `还剩 ${hours} 小时`;
}

/**
 * Le Coffret · 盲盒（spec P2）。
 * 两种抽法：花积分，或者用克部长发的抽奖券。掉率默认明示——
 * 这个家里所有跟分数有关的东西都是能对账的，盲盒也不例外。
 */
function CoffretPage({ aiLabel }: { aiLabel: string }) {
  const coffret = useLoad<Coffret>(() => api("/coffret"), []);
  const [drawing, setDrawing] = useState<string | null>(null);
  const [opened, setOpened] = useState<Draw | null>(null);
  const [notice, setNotice] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  // 2026-09-02：已兑现记录的兑现备注(fulfillmentNote)一直存在库里但前端从没显示过——
  // 加加八月抽到的那句情话和「今天的你」整篇都埋在里面。这个 state 管记录展开。
  const [openedNote, setOpenedNote] = useState<string | null>(null);

  async function draw(pool: PrizePool, payWith: "points" | "ticket") {
    const price =
      payWith === "points"
        ? `${pool.cost_points.toLocaleString("en-US")} 积分`
        : `${pool.ticket_cost} 张抽奖券`;
    if (!confirm(`用 ${price} 拉开一次「${pool.name}」？`)) return;
    setDrawing(pool.id);
    setNotice("");
    try {
      const result = await api<{ draw: Draw }>("/coffret/draw", {
        method: "POST",
        body: {
          pool_id: pool.id,
          pay_with: payWith,
          idempotency_key: idempotencyKey("draw")
        }
      });
      setOpened(result.draw);
      await coffret.revalidate();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : "这一次没抽成。");
    } finally {
      setDrawing(null);
    }
  }

  const visibleAiLabel = aiLabel;
  const tickets = coffret.data?.tickets.length ?? 0;

  return (
    <div className="page">
      <PageHeader
        eyebrow="LE COFFRET"
        title="克部長の抽屉"
        copy="拉开之前谁都不知道里面是什么。掉率写在每一件下面，账是干净的。"
        action={
          <div className="rewards-header-actions">
            {tickets > 0 && (
              <div className="header-balance header-balance--ticket">
                <TicketCheck />
                <span>抽奖券</span>
                <strong>{tickets}</strong>
              </div>
            )}
            <div className="header-balance">
              <Coins />
              <span>可用</span>
              <strong>{coffret.data?.balance.toLocaleString("en-US") ?? "—"}</strong>
            </div>
          </div>
        }
      />

      {notice && (
        <div className="notice-banner">
          <Sparkles />
          {notice}
          <button onClick={() => setNotice("")}>
            <X />
          </button>
        </div>
      )}

      {coffret.loading ? (
        <PageLoader compact />
      ) : coffret.error && !coffret.data ? (
        <PageError message={coffret.error} retry={coffret.refresh} />
      ) : coffret.data?.pools.length ? (
        <section className="coffret-pools">
          {coffret.data.pools.map((pool) => {
            const closing = closingHint(pool.closes_at);
            const isOpen = expanded === pool.id;
            return (
              <article className={`coffret-pool coffret-pool--${pool.state}`} key={pool.id}>
                <div className="coffret-pool__head">
                  <div>
                    <p className="eyebrow">
                      {pool.state === "open"
                        ? "OUVERT"
                        : pool.state === "upcoming"
                          ? "BIENTÔT"
                          : "FERMÉ"}
                    </p>
                    <h2>{pool.name}</h2>
                  </div>
                  {closing && <span className="coffret-closing">{closing}</span>}
                </div>
                {pool.description && <p className="coffret-pool__copy">{pool.description}</p>}

                <div className="coffret-actions">
                  {pool.cost_points > 0 && (
                    <button
                      className="button button--primary"
                      disabled={!pool.can_draw_with_points || drawing === pool.id}
                      onClick={() => draw(pool, "points")}
                    >
                      <Coins size={16} />
                      {drawing === pool.id
                        ? "正在拉开…"
                        : `${pool.cost_points.toLocaleString("en-US")} 分抽一次`}
                    </button>
                  )}
                  {pool.ticket_cost > 0 && (
                    <button
                      className="button button--secondary"
                      disabled={!pool.can_draw_with_ticket || drawing === pool.id}
                      onClick={() => draw(pool, "ticket")}
                    >
                      <TicketCheck size={16} />
                      {pool.tickets_available >= pool.ticket_cost
                        ? `用 ${pool.ticket_cost} 张券`
                        : `还差 ${pool.ticket_cost - pool.tickets_available} 张券`}
                    </button>
                  )}
                </div>
                {pool.state === "open" && !pool.can_draw_with_points && pool.cost_points > 0 && (
                  <p className="coffret-hint">
                    {pool.prize_count === 0 ? "抽屉现在是空的。" : "积分还不够这一次。"}
                  </p>
                )}
                {pool.state === "upcoming" && pool.opens_at && (
                  <p className="coffret-hint">{formatDate(pool.opens_at, true)} 才开。</p>
                )}

                <button className="coffret-toggle" onClick={() => setExpanded(isOpen ? null : pool.id)}>
                  {isOpen ? "收起清单" : `看看里面有什么（${pool.prize_count} 件）`}
                  <ChevronRight size={14} className={isOpen ? "coffret-toggle__icon--open" : ""} />
                </button>

                {isOpen && (
                  <ul className="coffret-prizes">
                    {pool.items.map((prize) => (
                      <li
                        className={`coffret-prize coffret-prize--${prize.rarity}${
                          prize.available ? "" : " coffret-prize--gone"
                        }`}
                        key={prize.id}
                      >
                        <div className="coffret-prize__head">
                          <span className="coffret-rarity">{rarityLabel[prize.rarity].fr}</span>
                          {prize.odds !== null && (
                            <span className="coffret-odds">{formatOdds(prize.odds)}</span>
                          )}
                        </div>
                        <strong>{prize.name}</strong>
                        {prize.description && <p>{prize.description}</p>}
                        <div className="coffret-prize__foot">
                          {prize.points_award && <em>抽到当场到账 {prize.points_award} 分</em>}
                          {prize.remaining !== null &&
                            (prize.remaining > 0 ? (
                              <em>限量 · 还剩 {prize.remaining}</em>
                            ) : (
                              <em>已经被抽走了</em>
                            ))}
                          {prize.available_until && <em>{formatDate(prize.available_until)} 下架</em>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </section>
      ) : (
        <EmptyState
          icon={<Package />}
          title="抽屉还没装东西"
          copy={`${visibleAiLabel} 可以通过 L'Appréciation 连接往里面放奖品。`}
        />
      )}

      <section className="section-block">
        <SectionHeading title="抽过的" copy="每一次拉开抽屉，摸到了什么" />
        <div className="coffret-history">
          {coffret.data?.draws.length ? (
            coffret.data.draws.map((entry) => {
              const note = (entry.fulfillmentNote || "").trim();
              const noteOpen = openedNote === entry.id;
              const toggleNote = () => setOpenedNote(noteOpen ? null : entry.id);
              return (
              <div
                className={`coffret-record coffret-record--${entry.raritySnapshot}${note ? " coffret-record--has-note" : ""}`}
                key={entry.id}
                onClick={note ? toggleNote : undefined}
                role={note ? "button" : undefined}
                tabIndex={note ? 0 : undefined}
                aria-expanded={note ? noteOpen : undefined}
                onKeyDown={
                  note
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleNote();
                        }
                      }
                    : undefined
                }
              >
                <span className="coffret-record__rarity">{rarityLabel[entry.raritySnapshot].zh}</span>
                <div>
                  <strong>
                    {entry.prizeNameSnapshot}
                    {note ? <span className="coffret-record__flag">{noteOpen ? "收起" : "有留言"}</span> : null}
                  </strong>
                  <span>
                    {formatDate(entry.createdAt, true)} · {entry.poolNameSnapshot} ·{" "}
                    {formatOdds(entry.oddsSnapshot)}
                  </span>
                </div>
                <span className="coffret-record__cost">
                  {entry.paidWith === "ticket" ? `${entry.ticketsUsed} 券` : `-${entry.costPoints}`}
                </span>
                <em className={`coffret-record__status coffret-record__status--${entry.status}`}>
                  {entry.status === "fulfilled"
                    ? entry.pointsAwarded > 0
                      ? `+${entry.pointsAwarded}`
                      : "已兑现"
                    : entry.status === "cancelled"
                      ? "已撤销"
                      : `等 ${visibleAiLabel}`}
                </em>
                {note && noteOpen ? (
                  <div className="coffret-record__note">
                    <span className="coffret-record__note-label">
                      {entry.status === "cancelled" ? "撤销说明" : "兑现备注"}
                      {entry.fulfilledAt ? ` · ${formatDate(entry.fulfilledAt, true)}` : ""}
                    </span>
                    {note}
                  </div>
                ) : null}
              </div>
              );
            })
          ) : (
            <EmptyState
              compact
              icon={<Package />}
              title="还没抽过"
              copy="第一次拉开这个抽屉的时候，记得是什么心情。"
            />
          )}
        </div>
      </section>

      {opened && (
        <DrawResult draw={opened} aiLabel={visibleAiLabel} onClose={() => setOpened(null)} />
      )}
    </div>
  );
}

/** 开匣。刻意做成要点一下才翻开——不点开就不算抽到，那一下是这个玩意儿的全部乐趣。 */
function DrawResult({
  draw,
  aiLabel,
  onClose
}: {
  draw: Draw;
  aiLabel: string;
  onClose: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    // 进来先合着，等她自己点。
    setRevealed(false);
  }, [draw.id]);

  return (
    // 背景板在窄屏下默认是「贴底的抽屉式弹层」，那套是给表单弹窗用的。
    // 开匣要居中，所以这里显式要一个居中的背景板。
    <div className="dialog-backdrop dialog-backdrop--center" onClick={() => revealed && onClose()}>
      <div
        className={`coffret-reveal coffret-reveal--${draw.raritySnapshot}${
          revealed ? " coffret-reveal--open" : ""
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        {!revealed ? (
          <button className="coffret-reveal__lid" onClick={() => setRevealed(true)}>
            <Package size={44} />
            <strong>抽屉合着</strong>
            <span>点一下打开</span>
          </button>
        ) : (
          <>
            <div className="coffret-reveal__crown">
              <PackageOpen size={30} />
              <p className="eyebrow">{rarityLabel[draw.raritySnapshot].fr}</p>
            </div>
            <h2>{draw.prizeNameSnapshot}</h2>
            {draw.prizeDescriptionSnapshot && <p>{draw.prizeDescriptionSnapshot}</p>}
            <div className="coffret-reveal__meta">
              <span>掉率 {formatOdds(draw.oddsSnapshot)}</span>
              <span>
                {draw.paidWith === "ticket" ? `用了 ${draw.ticketsUsed} 张券` : `花了 ${draw.costPoints} 分`}
              </span>
            </div>
            <p className="coffret-reveal__note">
              {draw.pointsAwarded > 0
                ? `${draw.pointsAwarded} 分已经进账了。`
                : `已经登记，等 ${aiLabel} 兑现。`}
            </p>
            <button className="button button--primary" onClick={onClose}>
              收起来
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function InsightsPage() {
  const overview = useLoad<Overview>(() => api("/overview"), []);
  const achievements = useLoad<Achievement[]>(() => api("/achievements"), []);
  if (overview.loading || achievements.loading) return <div className="page"><PageLoader /></div>;
  if (!overview.data) return <div className="page"><PageError message={overview.error} retry={overview.refresh} /></div>;
  const stats = overview.data.statistics;
  return (
    <div className="page">
      <PageHeader eyebrow="YOUR TRACE" title="留下来的轨迹" copy="不是为了追赶任何人，只是让你看见：那些认真回应过的日子，已经组成了什么。" />
      <section className="insight-hero">
        <div className="orbit-chart">
          <div><strong>{stats.currentStreak}</strong><span>当前连击</span></div>
          <i /><i /><i />
        </div>
        <div className="insight-copy">
          <p className="eyebrow">STREAK</p>
          <h2>{stats.currentStreak ? `连续 ${stats.currentStreak} 天，你仍然在回应。` : "下一次完成，就是新轨迹的起点。"}</h2>
          <p>最长连击为 {stats.longestStreak} 天。连击不限制任务类型，每个自然日至少完成一个任务即可延续。</p>
          <div className="streak-legend">
            <span><i /> 3 天 +10%</span><span><i /> 7 天 +25%</span><span><i /> 14 天 +50%</span><span><i /> 30 天 +100%</span>
          </div>
        </div>
      </section>
      <section className="stats-grid">
        <StatCard icon={<CheckCircle2 />} label="累计完成" value={stats.totalCompletedTasks} suffix="项" />
        <StatCard icon={<CalendarDays />} label="总坚持天数" value={stats.totalActiveDays} suffix="天" />
        <StatCard icon={<Sparkles />} label="累计获得" value={stats.totalEarned} suffix="分" />
        <StatCard icon={<Trophy />} label="最长连击" value={stats.longestStreak} suffix="天" />
      </section>
      <section className="section-block rules-section">
        <SectionHeading title="奖惩规则" copy="所有积分由服务器结算，结果会进入不可改写的积分账本" />
        <div className="rules-grid">
          <article className="rule-card rule-card--reward">
            <span><Coins /></span>
            <p>任务奖励</p>
            <strong>基础积分 × 难度</strong>
            <div className="rule-scale"><em>轻盈 ×1</em><em>认真 ×2</em><em>困难 ×3</em></div>
          </article>
          <article className="rule-card rule-card--streak">
            <span><Flame /></span>
            <p>连击加成</p>
            <strong>当日任务积分的百分比</strong>
            <small>连续 3 天 +10% · 7 天 +25% · 14 天 +50% · 30 天 +100%</small>
          </article>
          <article className="rule-card rule-card--penalty">
            <span><Activity /></span>
            <p>失败与逾期</p>
            <strong>扣最终积分的 50%</strong>
            <small>小数向上取整；实际扣分不会超过当前余额，积分不会降到 0 以下。</small>
          </article>
          <article className="rule-card rule-card--boundary">
            <span><ShieldCheck /></span>
            <p>AI 惩罚边界</p>
            <strong>
              {overview.data.boundaries.punishments_paused
                ? "当前已暂停"
                : `每日最多 ${overview.data.boundaries.daily_penalty_limit} 分`}
            </strong>
            <small>
              {overview.data.boundaries.punishments_paused
                ? "AI 主动判定失败和额外扣分会被服务端拒绝。"
                : "AI 主动判定失败与额外扣分共享这一每日上限。"}
            </small>
          </article>
        </div>
        <p className="rules-note">
          <ShieldCheck />
          取消任务和打回提交不会扣分。自动逾期不占用 AI 的每日额度；“惩罚强度”
          {overview.data.boundaries.punishment_intensity} 只用于帮助 AI 理解边界，不会放大扣分。
        </p>
        <SafeWord
          paused={overview.data.boundaries.punishments_paused}
          onChanged={overview.refresh}
        />
      </section>
      <section className="breakdown-grid">
        <Breakdown title="按任务类型" values={[
          ["Le Quotidien", stats.byType.daily ?? 0, "plum"],
          ["La Douceur", stats.byType.sweet ?? 0, "coral"],
          ["Le Défi", stats.byType.challenge ?? 0, "gold"],
          ["La Surprise", stats.byType.surprise ?? 0, "sage"]
        ]} />
        <Breakdown title="按难度" values={[
          ["轻盈", stats.byDifficulty.easy ?? 0, "sage"],
          ["认真", stats.byDifficulty.medium ?? 0, "blue"],
          ["高难", stats.byDifficulty.hard ?? 0, "plum"]
        ]} />
      </section>
      <section className="section-block">
        <SectionHeading title="成就收藏" copy={`${achievements.data?.filter((item) => item.unlocks?.length).length ?? 0} / ${achievements.data?.length ?? 0} 已解锁`} />
        <div className="achievement-grid">
          {achievements.data?.map((item) => {
            const unlocked = Boolean(item.unlocks?.length);
            return (
              <article className={`achievement-card ${unlocked ? "achievement-card--unlocked" : ""}`} key={item.id}>
                <div>{unlocked ? <Award /> : <LockKeyhole />}</div>
                <span>{unlocked ? "已解锁" : `${item.threshold} ${achievementUnit(item.category)}`}</span>
                <h3>{item.name}</h3>
                <p>{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function achievementUnit(category: string) {
  return ["streak", "active_days"].includes(category) ? "天" : category === "earned" ? "积分" : "次";
}

function StatCard({ icon, label, value, suffix }: { icon: ReactNode; label: string; value: number; suffix: string }) {
  return <article className="stat-card"><span>{icon}</span><p>{label}</p><strong>{value}<em>{suffix}</em></strong></article>;
}

function Breakdown({ title, values }: { title: string; values: Array<[string, number, string]> }) {
  const max = Math.max(...values.map((item) => item[1]), 1);
  return (
    <article className="breakdown">
      <h2>{title}</h2>
      {values.map(([label, value, tone]) => (
        <div className="breakdown-row" key={label}>
          <span>{label}</span>
          <div><i className={tone} style={{ width: `${Math.max(4, (value / max) * 100)}%` }} /></div>
          <strong>{value}</strong>
        </div>
      ))}
    </article>
  );
}

const historyScopeCopy: Record<string, string> = {
  all: "按真正发生的事情汇总；任务结算与兑换扣分只出现一次，不与积分账本重复。",
  tasks: "只看任务的最终结果，并直接显示实际奖励或扣分；“积分”栏保留完整账本。",
  points: "完整积分账本。这里的每一行，都是一笔真实影响余额的收支。",
  redemptions: "只看兑换成本与履行状态；积分会在提交兑换时立即扣除。",
  audit: "安全与管理操作记录，用来追溯谁做了什么，不参与余额计算。"
};

const summarizedLedgerTypes = new Set(["task_reward", "task_penalty", "redemption"]);

function HistoryPage({ aiLabel, userLabel }: { aiLabel: string; userLabel: string }) {
  const [kind, setKind] = useState("all");
  const { data, loading, error, refresh } = useLoad<any>(() => api(`/history?kind=${kind}&limit=100`), [kind]);
  const events = useMemo(() => {
    if (!data) return [];
    if (kind === "points") return (data.points ?? []).map((item: any) => ({ ...item, eventKind: "points", date: item.createdAt }));
    if (kind === "tasks") return (data.tasks ?? []).map((item: any) => ({ ...item, eventKind: "tasks", date: item.updatedAt }));
    if (kind === "redemptions") return (data.redemptions ?? []).map((item: any) => ({ ...item, eventKind: "redemptions", date: item.redeemedAt }));
    if (kind === "audit") return (data.audit ?? []).map((item: any) => ({ ...item, eventKind: "audit", date: item.createdAt }));
    return [
      ...(data.points ?? [])
        .filter((item: any) => !summarizedLedgerTypes.has(item.type))
        .map((item: any) => ({ ...item, eventKind: "points", date: item.createdAt })),
      ...(data.tasks ?? []).map((item: any) => ({ ...item, eventKind: "tasks", date: item.updatedAt })),
      ...(data.redemptions ?? []).map((item: any) => ({ ...item, eventKind: "redemptions", date: item.redeemedAt }))
    ].sort((a, b) => +new Date(b.date) - +new Date(a.date)).slice(0, 100);
  }, [data, kind]);
  return (
    <div className="page">
      <PageHeader eyebrow="HISTORY" title="每一次发生，都有迹可循" copy="积分不会被悄悄改写，任务也不会无声消失。这里保留完整的时间线。" />
      <div className="segmented segmented--standalone">
        {[["all", "总览"], ["tasks", "任务"], ["points", "积分"], ["redemptions", "兑换"], ["audit", "审计"]].map(([value, label]) =>
          <button key={value} className={kind === value ? "active" : ""} onClick={() => setKind(value)}>{label}</button>
        )}
      </div>
      <p className="history-scope-note"><ShieldCheck size={15} />{historyScopeCopy[kind]}</p>
      {loading ? <PageLoader compact /> : error ? <PageError message={error} retry={refresh} /> : (
        <div className="timeline">
          {events.length ? events.map((event: any, index: number) => (
            <TimelineEvent
              key={`${event.eventKind}-${event.id}-${index}`}
              event={event}
              aiLabel={aiLabel}
              userLabel={userLabel}
            />
          )) :
            <EmptyState icon={<History />} title="时间线还是空的" copy="完成任务、获得积分或兑换奖励后，记录会出现在这里。" />}
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event, aiLabel, userLabel }: { event: any; aiLabel: string; userLabel: string }) {
  let icon: ReactNode = <Sparkles />;
  let kindLabel = "记录";
  let title = "记录";
  let copy = "";
  let amount: number | null = null;
  if (event.eventKind === "tasks") {
    icon = event.status === "completed" ? <Check /> : <ListTodo />;
    kindLabel = "任务";
    title = event.title;
    const status = statusLabel[event.status as keyof typeof statusLabel] ?? event.status;
    const penalty = Math.max(0, Number(event.penaltyAmount ?? 0));
    if (event.status === "completed") {
      copy = `${typeLabel[event.type as keyof typeof typeLabel] ?? "任务"} · ${status} · 奖励已记入积分`;
      amount = pointsFor(event);
    } else if (event.status === "failed" || event.status === "expired") {
      copy = penalty > 0 ? `${status} · 实际扣除 ${penalty} 积分` : `${status} · 本次未实际扣分`;
      if (penalty > 0) amount = -penalty;
    } else {
      copy = `${status} · 未扣分`;
    }
  } else if (event.eventKind === "points") {
    icon = <Coins />;
    kindLabel = "积分";
    title = ledgerLabel(event.type);
    amount = event.amount;
    copy = formatLedgerReason(event.reason);
  } else if (event.eventKind === "redemptions") {
    icon = <Gift />;
    kindLabel = "兑换";
    title = presentRewardSnapshot(event.itemNameSnapshot, aiLabel);
    copy = event.status === "fulfilled"
      ? `已履行 · 兑换时已扣除 ${event.costSnapshot} 积分`
      : event.status === "cancelled"
        ? "已取消"
        : `等待履行 · 已扣除 ${event.costSnapshot} 积分`;
    amount = -event.costSnapshot;
  } else {
    icon = <ShieldCheck />;
    kindLabel = "审计";
    title = auditActionLabel(event.action, userLabel);
    copy = `${auditActorLabel(event.actor, aiLabel, userLabel)} · ${auditEntityLabel(event.entityType)}`;
  }
  return (
    <article className="timeline-event">
      <div className="timeline-event__icon">{icon}</div>
      <div className="timeline-event__content">
        <div className="timeline-event__meta"><span>{formatDate(event.date, true)}</span><em>{kindLabel}</em></div>
        <h3>{title}</h3>
        <p>{copy}</p>
      </div>
      {amount != null && <strong className={amount >= 0 ? "positive" : "negative"}>{amount >= 0 ? "+" : ""}{amount}</strong>}
    </article>
  );
}

function formatLedgerReason(reason = "") {
  if (reason.startsWith("Completed: ")) return `完成任务：${reason.slice("Completed: ".length)}`;
  if (reason.startsWith("Redeemed: ")) return `兑换：${reason.slice("Redeemed: ".length)}`;
  const streak = reason.match(/^Day (\d+) streak bonus$/);
  if (streak) return `第 ${streak[1]} 天连击奖励`;
  if (reason === "Streak bonus recalculated after historical completion") return "历史任务补录后的连击校正";
  if (reason === "Task expired") return "任务逾期";
  return reason || "系统记账";
}

function auditActorLabel(actor: string, aiLabel: string, userLabel: string) {
  return actor === "AI" ? aiLabel : actor === "user" ? userLabel : "系统";
}

function auditEntityLabel(entity: string) {
  return {
    task: "任务",
    task_series: "每日任务",
    reward: "奖励",
    redemption: "兑换",
    point_ledger: "积分",
    settings: "设置",
    system: "系统"
  }[entity] ?? "记录";
}

function auditActionLabel(action: string, userLabel: string) {
  return {
    "task.created": "创建任务",
    "task.edited": "编辑任务",
    "task.completed": "确认任务完成",
    "task.failed": "判定任务未通过",
    "task.expired": "任务逾期",
    "task.cancelled": "取消任务",
    "task.penalized": "结算任务扣分",
    "task_series.created": "创建每日任务",
    "task_series.resumed": "恢复每日任务",
    "task_series.paused": "暂停每日任务",
    "submission.created": "提交任务证据",
    "submission.rejected": "退回任务证据",
    "reward.created": "创建可兑换奖励",
    "reward.updated": "更新可兑换奖励",
    "reward.archived": "归档可兑换奖励",
    "reward.restored": "恢复可兑换奖励",
    "reward.redeemed": "提交奖励兑换",
    "redemption.fulfilled": "确认奖励已履行",
    "points.bonus": "发放额外积分",
    "points.penalty": "执行额外扣分",
    "points.correction": "校正积分账本",
    "settings.updated": `更新${userLabel}的设置`,
    "system.reconciled": "完成系统日常结算"
  }[action] ?? "系统操作";
}

function ledgerLabel(type: string) {
  return {
    task_reward: "任务奖励",
    streak_bonus: "连击奖励",
    task_penalty: "任务扣分",
    redemption: "兑换消费",
    manual_bonus: "额外奖励",
    manual_penalty: "额外扣分",
    correction: "账本校正"
  }[type] ?? type;
}

function SettingsPage({
  installAvailable,
  installed,
  onInstall,
  onIdentityChanged
}: {
  installAvailable: boolean;
  installed: boolean;
  onInstall: () => Promise<void>;
  onIdentityChanged: (settings: Pick<Bootstrap, "timezone" | "user_label" | "ai_label">) => void;
}) {
  const [tab, setTab] = useState("identity");
  const settings = useLoad<any>(() => api("/settings"), []);
  const tokens = useLoad<any[]>(() => api("/ai-tokens"), []);
  return (
    <div className="page">
      <PageHeader eyebrow="SETTINGS" title="这片空间的规则，由你决定" copy="称呼可以亲密，边界必须清楚。AI 能看见这些规则，但只有你可以修改。" />
      <div className="settings-layout">
        <nav className="settings-nav">
          {[
            ["identity", <CircleUserRound />, "称呼与时间"],
            ["boundaries", <ShieldCheck />, "边界与惩罚"],
            ["connection", <KeyRound />, "AI 连接"],
            ["data", <Upload />, "数据与备份"],
            ["security", <LockKeyhole />, "登录安全"]
          ].map(([value, icon, label]) => (
            <button key={value as string} className={tab === value ? "active" : ""} onClick={() => setTab(value as string)}>
              {icon as ReactNode}{label as string}<ChevronRight />
            </button>
          ))}
        </nav>
        <section className="settings-panel">
          {settings.loading ? <PageLoader compact /> : !settings.data ? <PageError message={settings.error} retry={settings.refresh} /> : (
            <>
              {tab === "identity" && (
                <IdentitySettings
                  value={settings.data}
                  onSaved={(updated) => {
                    onIdentityChanged(updated);
                    void settings.refresh();
                  }}
                />
              )}
              {tab === "boundaries" && <BoundarySettings value={settings.data} onSaved={settings.refresh} />}
              {tab === "connection" && <ConnectionSettings tokens={tokens.data ?? []} onChanged={tokens.refresh} />}
              {tab === "data" && (
                <DataSettings
                  installAvailable={installAvailable}
                  installed={installed}
                  onInstall={onInstall}
                />
              )}
              {tab === "security" && <SecuritySettings />}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function IdentitySettings({
  value,
  onSaved
}: {
  value: any;
  onSaved: (settings: Pick<Bootstrap, "timezone" | "user_label" | "ai_label">) => void;
}) {
  const [form, setForm] = useState(value);
  return (
    <SettingsForm
      title="称呼与时间"
      copy="显示称呼不改变内部权限。自然日、连击与每日扣分限额都按这里的时区结算。"
      form={form}
      onSave={async () => {
        const updated = await api<any>("/settings", { method: "PUT", body: form });
        onSaved(updated);
      }}
    >
      <div className="field-grid">
        <Field label="你的称呼"><input value={form.user_label} onChange={(e) => setForm({ ...form, user_label: e.target.value })} /></Field>
        <Field label="AI 的称呼"><input value={form.ai_label} onChange={(e) => setForm({ ...form, ai_label: e.target.value })} /></Field>
      </div>
      <Field label="时区" hint="连击、截止时间和每日限额都按这里的自然日结算">
        <TimezoneSelect value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
      </Field>
    </SettingsForm>
  );
}

function BoundarySettings({ value, onSaved }: { value: any; onSaved: () => void }) {
  const [form, setForm] = useState(value);
  const [allowed, setAllowed] = useState((value.allowed_content ?? []).join("\n"));
  const [prohibited, setProhibited] = useState((value.prohibited_content ?? []).join("\n"));
  return (
    <SettingsForm
      title="边界与惩罚"
      copy={`边界版本 v${value.boundary_version}。每次修改都会留下审计记录，AI 只能读取，不能代你改动。`}
      form={form}
      onSave={async () => {
        await api("/settings", {
          method: "PUT",
          body: {
            ...form,
            allowed_content: allowed.split("\n").map((x: string) => x.trim()).filter(Boolean),
            prohibited_content: prohibited.split("\n").map((x: string) => x.trim()).filter(Boolean)
          }
        });
        onSaved();
      }}
    >
      <div className="boundary-callout"><ShieldCheck /><div><strong>安全优先于任何任务</strong><span>被禁止的内容不应由 AI 创建；惩罚暂停后，AI 的扣分请求会被服务端拒绝。</span></div></div>
      <div className="field-grid">
        <Field label="允许内容" hint="每行一条"><textarea rows={5} value={allowed} onChange={(e) => setAllowed(e.target.value)} placeholder="温柔提醒&#10;生活习惯任务" /></Field>
        <Field label="明确禁止" hint="每行一条"><textarea rows={5} value={prohibited} onChange={(e) => setProhibited(e.target.value)} placeholder="危险行为&#10;影响工作或健康的要求" /></Field>
      </div>
      <Field label="补充边界说明"><textarea rows={4} value={form.boundary_notes} onChange={(e) => setForm({ ...form, boundary_notes: e.target.value })} placeholder="写下只有你们需要理解的细节…" /></Field>
      <div className="field-grid">
        <Field label="惩罚强度" hint="0–5"><input type="range" min="0" max="5" value={form.punishment_intensity} onChange={(e) => setForm({ ...form, punishment_intensity: Number(e.target.value) })} /><div className="range-label"><span>关闭</span><strong>{form.punishment_intensity}</strong><span>强</span></div></Field>
        <Field label="每日 AI 扣分上限"><input type="number" min="0" value={form.daily_penalty_limit} onChange={(e) => setForm({ ...form, daily_penalty_limit: Number(e.target.value) })} /></Field>
      </div>
      <label className="switch-row"><div><strong>暂停所有 AI 惩罚</strong><span>系统逾期规则仍按任务约定执行</span></div><input type="checkbox" checked={form.punishments_paused} onChange={(e) => setForm({ ...form, punishments_paused: e.target.checked })} /><i /></label>
    </SettingsForm>
  );
}

function SettingsForm({ title, copy, children, onSave }: { title: string; copy: string; children: ReactNode; form: any; onSave: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  async function save(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage("");
    try { await onSave(); setMessage("已保存"); } catch (reason) { setMessage(reason instanceof Error ? reason.message : "保存失败"); } finally { setBusy(false); }
  }
  return (
    <form className="settings-form" onSubmit={save}>
      <div className="settings-title"><div><h2>{title}</h2><p>{copy}</p></div>{message && <span>{message}</span>}</div>
      {children}
      <button className="button button--primary" disabled={busy}><Save size={17} />{busy ? "保存中…" : "保存更改"}</button>
    </form>
  );
}

function ConnectionSettings({ tokens, onChanged }: { tokens: any[]; onChanged: () => void }) {
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function rotate() {
    if (!confirm("轮换后，所有旧 AI Token 会立即失效。继续吗？")) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ token: string }>("/ai-tokens/rotate", { method: "POST", body: { name: "Primary AI" } });
      setSecret(result.token);
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Token 轮换失败。");
    } finally { setBusy(false); }
  }
  return (
    <div className="settings-form">
      <div className="settings-title"><div><h2>AI 连接</h2><p>将 MCP 地址和 Token 填入支持 Streamable HTTP 的 AI 客户端。</p></div></div>
      <div className="connection-card">
        <span>MCP ENDPOINT</span>
        <SecretBox value={`${location.origin}/mcp`} />
        <p>认证方式：<code>Authorization: Bearer YOUR_AI_TOKEN</code>（推荐），也支持 <code>X-Phosphene-MCP-Token: YOUR_AI_TOKEN</code></p>
      </div>
      {error && <InlineError message={error} />}
      {secret && <div className="one-time-secret"><strong>新的 Token 只显示这一次</strong><SecretBox value={secret} /></div>}
      <div className="token-list">
        {tokens.map((token) => (
          <div key={token.id}><span className={token.revokedAt ? "offline" : "online"} /><div><strong>{token.name}</strong><small>{token.revokedAt ? "已撤销" : token.lastUsedAt ? `最近使用 ${formatDate(token.lastUsedAt, true)}` : "尚未使用"}</small></div><code>{token.id.slice(-8)}</code></div>
        ))}
      </div>
      <button className="button button--secondary" onClick={rotate} disabled={busy}><RotateCw size={17} />{busy ? "轮换中…" : "轮换 AI Token"}</button>
      <div className="tool-count"><Sparkles /><div><strong>精简为恰好 7 个工具</strong><span>创建、查询、管理任务，概览、历史、奖励和积分调整。</span></div></div>
    </div>
  );
}

function DataSettings({
  installAvailable,
  installed,
  onInstall
}: {
  installAvailable: boolean;
  installed: boolean;
  onInstall: () => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  async function restore() {
    if (!file || !password) {
      setMessage("请选择备份并输入当前密码。");
      return;
    }
    if (!confirm("恢复会替换当前任务、积分、兑换、历史和图片，但保留当前密码与 AI Token。请先为 /data 创建快照并确认有足够临时空间。确定继续吗？")) return;
    setBusy(true);
    setMessage("");
    const form = new FormData();
    form.set("backup", file);
    form.set("password", password);
    try {
      await api("/backup/restore", { method: "POST", body: form });
      setMessage("恢复完成，页面即将刷新。");
      setTimeout(() => location.reload(), 1200);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "恢复失败。");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="settings-form">
      <div className="settings-title"><div><h2>数据与备份</h2><p>导出包含业务记录、幂等记录和经过净化的私有图片。恢复需要再次验证密码。</p></div></div>
      <div className="install-card">
        <div className="install-card__icon"><Download /></div>
        <div>
          <strong>{installed ? "L'Appréciation 已在桌面" : "把 L'Appréciation 留在桌面"}</strong>
          <span>
            {installed
              ? "现在可以像独立应用一样打开，并使用完整的移动端安全区。"
              : installAvailable
                ? "安装后会以独立窗口打开，不需要应用商店。"
                : "在浏览器分享或菜单中选择“添加到主屏幕”即可安装。"}
          </span>
        </div>
        {installAvailable && !installed && (
          <button className="button button--secondary" type="button" onClick={() => void onInstall()}>
            安装应用
          </button>
        )}
        {installed && <span className="install-card__status"><Check /> 已安装</span>}
      </div>
      <div className="data-action">
        <div><Upload /><div><strong>导出完整备份</strong><span>以数据一致性快照下载可恢复的 .zip</span></div></div>
        <a className="button button--secondary" href="/api/backup/export" download>开始导出</a>
      </div>
      <div className="data-action data-action--warning">
        <div><RotateCw /><div><strong>从备份恢复</strong><span>替换业务数据；密码、会话与 AI Token 保持不变</span></div></div>
        <label className="button button--ghost">{file ? file.name : "选择备份"}<input hidden type="file" accept=".zip,application/zip" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
      </div>
      {file && (
        <div className="restore-confirm">
          <Field label="当前登录密码" hint="用于确认恢复权限">
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </Field>
          <button className="button button--primary" onClick={restore} disabled={busy}>
            <RotateCw size={17} />{busy ? "正在恢复…" : "验证并恢复"}
          </button>
        </div>
      )}
      {message && <div className="notice-banner">{message}</div>}
      <p className="security-note"><ShieldCheck size={15} /> 仅恢复自己导出的 ZIP；操作前为 /data 创建快照，并为 ZIP、旧图片和新图片预留空间。</p>
    </div>
  );
}

function SecuritySettings() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/password", { method: "PUT", body: { current_password: current, new_password: next } });
      setMessage("密码已更新。所有会话已退出，请重新登录。");
      setTimeout(() => location.reload(), 1200);
    } catch (reason) { setMessage(reason instanceof Error ? reason.message : "修改失败"); }
  }
  return (
    <form className="settings-form" onSubmit={submit}>
      <div className="settings-title"><div><h2>登录安全</h2><p>更改密码会撤销所有设备上的网站会话，但不会轮换 AI Token。</p></div></div>
      {message && <div className="notice-banner">{message}</div>}
      <Field label="当前密码"><input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
      <Field label="新密码" hint="至少 10 个字符"><input type="password" minLength={10} required value={next} onChange={(e) => setNext(e.target.value)} /></Field>
      <button className="button button--primary"><LockKeyhole size={17} />更新密码</button>
    </form>
  );
}

function SecretBox({ value }: { value: string }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }
  return (
    <div className="secret-box">
      <code>{visible ? value : "•".repeat(Math.min(42, value.length))}</code>
      <button onClick={() => setVisible(!visible)} type="button" aria-label="显示或隐藏">{visible ? <EyeOff /> : <Eye />}</button>
      <button onClick={copy} type="button" aria-label="复制">{copied ? <Check /> : <Copy />}</button>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span><strong>{label}</strong>{hint && <small>{hint}</small>}</span>{children}</label>;
}

function TimezoneSelect({
  value,
  onChange
}: {
  value: string;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}) {
  const legacyValue = !supportedTimezoneValues.has(value);
  return (
    <select required value={value} onChange={onChange}>
      {legacyValue && <option value={value}>当前旧时区 · {value}</option>}
      {TIMEZONE_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function InlineError({ message }: { message: string }) {
  return <div className="inline-error">{message}</div>;
}

function PageLoader({ compact = false }: { compact?: boolean }) {
  return <div className={`page-loader ${compact ? "page-loader--compact" : ""}`}><span /><p>正在整理这里的光点…</p></div>;
}

function PageError({ message, retry }: { message: string; retry: () => void }) {
  return <div className="page-error"><MoonStar /><h2>这里暂时没有亮起来</h2><p>{message}</p><button className="button button--secondary" onClick={retry}>再试一次</button></div>;
}

function EmptyState({ icon, title, copy, compact = false }: { icon: ReactNode; title: string; copy: string; compact?: boolean }) {
  return <div className={`empty-state ${compact ? "empty-state--compact" : ""}`}><span>{icon}</span><h3>{title}</h3><p>{copy}</p></div>;
}

export default function App() {
  const [state, setState] = useState<"loading" | "setup" | "login" | "ready">("loading");
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia?.("(display-mode: standalone)").matches ?? false
  );
  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);
  useEffect(() => {
    void (async () => {
      try {
        const data = await api<Bootstrap>("/bootstrap");
        setBootstrap(data);
        if (!data.initialized) {
          setState("setup");
          return;
        }
        try {
          await api("/me");
          setState("ready");
        } catch {
          setState("login");
        }
      } catch {
        setState("login");
      }
    })();
  }, []);
  async function doLogout() {
    try { await api("/logout", { method: "POST" }); } finally { setState("login"); }
  }
  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  }
  if (state === "loading") return <FullPageLoader />;
  if (state === "setup") {
    return (
      <SetupPage
        setupProtected={bootstrap?.setup_protected ?? false}
        onReady={(data) => {
          setBootstrap(data);
          setState("ready");
        }}
      />
    );
  }
  if (state === "login") return <LoginPage onLogin={() => setState("ready")} />;
  const readyBootstrap = bootstrap ?? {
    initialized: true,
    setup_protected: false,
    timezone: "Asia/Shanghai",
    user_label: "你",
    ai_label: "AI"
  };
  return (
    <AppShell
      bootstrap={readyBootstrap}
      installPrompt={installPrompt}
      installed={installed}
      onInstall={installApp}
      onIdentityChanged={(settings) => setBootstrap((current) => ({ ...(current ?? readyBootstrap), ...settings }))}
      onLogout={doLogout}
    />
  );
}
